import { CommonModule } from '@angular/common';
import { AfterViewInit, inject, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import moment, { Moment } from 'moment';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { allQualifications } from '../../new-rep-application-interfaces/qualifications.interface';
import { viewAttachements } from '../../new-rep-application-interfaces/attachements.interface';
import { NewRepApplicationService } from '../../new-rep-application-services/new-rep-application.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmPopupComponent } from '../../confirm-popup/confirm-popup.component';
import { SnackbarService } from '../../../../../shared/services/snackbar/snackbar.service';
import { HttpStatusCode } from 'axios';
import { SessionStorageService } from '../../../../../core/services/session-storage/session-storage.service';
import { noWhitespace } from '../../../../../shared/validators/no-whitespace.validator';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'app-academic-qualifications',
  providers: [provideMomentDateAdapter(MY_FORMATS)],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatNativeDateModule,
    CommonModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
  ],
  templateUrl: './academic-qualifications.component.html',
  styleUrl: './academic-qualifications.component.scss'
})
export class AcademicQualificationsComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() controlName: string = 'academicqualifications';
  @Input() academicqualifications: allQualifications[] = [];
  @Input() viewattachments!: viewAttachements[];
  @Input() readonly = signal(false);
  @Output() next = new EventEmitter<string[]>();
  @Output() preview = new EventEmitter<string[]>();

  private fb = inject(FormBuilder);
  private readonly matDialog = inject(MatDialog);
  private newRepApplicationService = inject(NewRepApplicationService);
  private snackBarService = inject(SnackbarService);
  private sessionStorageService = inject(SessionStorageService);
  readonly message = 'This field is required.';
  status = 'Not Started';

  async ngOnInit(): Promise<void> {
    //  Use dynamic controlName (reusable fix)
    if (!this.formGroup.get(this.controlName)) {
      this.formGroup.addControl(
        this.controlName,
        this.fb.array([])
      );
    }

    //  Always show at least one row
    if (this.formArray.length === 0) {
      this.addRow();
    }

    await this.fetchAcadmicQualifications();
    this.handleFormStatus();

  }

  fetchAcadmicQualifications(): void {
    const formArray = this.formGroup.get('academicqualifications') as FormArray;

    this.academicqualifications.forEach((item, index) => {
      if (index !== 0) {
        this.addRow();
      }
      formArray.at(index).patchValue({
        startDate: item.startDate,
        endDate: item.endDate,
        qualification: item.qualificationType,
        institution: item.institutionName,
        transcriptfilecheck: item.hasTranscript == true ? (this.onCheckboxChange(true, 'transcriptfilecheck', 'transcriptfileName', index), item.hasTranscript) : item.hasTranscript,
        certificatefilecheck: item.hasCertificate == true ? (this.onCheckboxChange(true, 'certificatefilecheck', 'certificatefileName', index), item.hasCertificate) : item.hasCertificate
      });
      this.viewattachments.forEach(item => {
        if (item?.onboardingDocumentType === 'academic_transcript' && index === Number(item?.responseValue)) {
          formArray.at(index).patchValue({
            transcriptfileName: item.fileName
          })

        } else if (item?.onboardingDocumentType === 'Academic' && index === Number(item?.responseValue)) {
          formArray.at(index).patchValue({
            certificatefileName: item.fileName
          })
        }
      });
    });
    if (this.formGroup.valid) {
      this.status = 'Completed';
      this.next.emit([this.status, 'submitted']);
    } else if (this.formGroup.invalid) {
      const anyFilled = this.formArray.value.some((group: { [s: string]: unknown; } | ArrayLike<unknown>) =>
        Object.values(group).some(
          v => v && v.toString().trim() !== ''
        )
      );
      if (anyFilled) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'submitted']);
      } else {
        this.status = 'Not Started';
        this.next.emit([this.status, 'submitted']);
      }
    }
    if (this.readonly() == true) {
      this.formGroup?.disable();
    } else {
      this.formGroup?.enable();
    }
  }


  //  Generic FormArray getter
  get formArray(): FormArray {
    return this.formGroup.get(this.controlName) as FormArray;
  }

  //  Create row
  createRow(): FormGroup {
    return this.fb.group({
      startDate: null,
      endDate: null,
      qualification: ['', [Validators.required,noWhitespace]],
      institution: ['', [Validators.required,noWhitespace]],
      transcriptfilecheck: false,
      transcriptfile: [null],
      transcriptfileName: ['', [Validators.required]],
      certificatefilecheck: false,
      certificatefile: [null],
      certificatefileName: ['', [Validators.required]]

    }, { validators: this.dateRangeValidator });
  }

  //  Add row
  addRow(): void {
    this.formArray.push(this.createRow());
  }

  onCheckboxChange(checked: boolean, checkfield: any, field: any, index: number) {
    if (checked) {
      this.formArray.at(index).get(checkfield)?.setValue(true);
      const control = this.formArray.at(index).get(field);
      control?.clearValidators();
      control?.reset();
      control?.updateValueAndValidity(); // ✅ IMPORTANT

    } else {
      this.formArray.at(index).get(checkfield)?.setValue(false);
      const control = this.formArray.at(index).get(field);
      control?.setValidators([Validators.required]);
      control?.updateValueAndValidity(); // ✅ IMPORTANT
    }
  }

  dateRangeValidator(group: FormGroup) {
    const start = group.get('startDate')?.value; // format: "2026-04"
    const end = group.get('endDate')?.value;

    if (!start || !end) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);

    return endDate >= startDate ? null : { invalidDateRange: true };
  }

  //  Remove row (keep at least one row)
  async removeRow(index: number): Promise<void> {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to delete the Qualification?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.formArray.length > 1) {
          if (this.academicqualifications[index]?.id != undefined) {
            let id = this.academicqualifications[index]?.id;

            const responseData = await this.newRepApplicationService
              .deleteQualification(id)
              .then((response) => {
                return JSON.parse(response);
              })
              .catch((error) => {
                // console.error(error);
                return error;
              });
          }
          this.viewDocumants();
          if (this.formArray.at(index).get('transcriptfileName')?.value) {
            let id = 0;
            let docTypeId = 0;
            let onboardingId = 0;
            if (this.viewattachments) {

              this.viewattachments.forEach(item => {
                if (item?.onboardingDocumentType === 'academic_transcript' && index === Number(item?.responseValue)) {
                  id = item.id,
                    docTypeId = item.docTypeId,
                    onboardingId = item.onboardingId
                }
              });
              this.deleteDocument(onboardingId, docTypeId, id);
            }
          }
          if (this.formArray.at(index).get('certificatefileName')?.value) {
            let id = 0;
            let docTypeId = 0;
            let onboardingId = 0;
            if (this.viewattachments) {
              this.viewattachments.forEach(item => {
                if (item?.onboardingDocumentType === 'Academic' && index === Number(item?.responseValue)) {
                  id = item.id,
                    docTypeId = item.docTypeId,
                    onboardingId = item.onboardingId
                }
              });
              this.deleteDocument(onboardingId, docTypeId, id);
            }
          }

          this.formArray.removeAt(index);
        } else {
          // console.log('User cancelled');
        }
      }
    });


  }

  async deleteDocument(onboardingId: number, docTypeId: number, id: number) {
    const data = {
      onboardingId: onboardingId,
      docTypeId: docTypeId,
      id: id
    }
    const responseData = await this.newRepApplicationService
      .deletedocument(data)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
  }


  //  File upload handler with preview
  async ontranscriptFileChange(event: any, index: number): Promise<void> {
    const file = event.target.files?.[0];
    const fileControl = this.formArray.at(index).get('transcriptfileName');
    if (file) {
      this.formArray.at(index).patchValue({
        transcriptfile: file,
        transcriptfileName: file.name
      });
    }
    fileControl?.markAsTouched();
    fileControl?.updateValueAndValidity();
    if (!file) {
      fileControl?.setValue(null);
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // ❌ Invalid type
    if (!allowedTypes.includes(file.type)) {
      fileControl?.setErrors({ invalidType: true });
      return;
    }

    // ❌ Invalid size
    if (file.size > maxSize) {
      fileControl?.setErrors({ maxSize: true });
      return;
    }

    // ✅ Valid file
    fileControl?.setErrors(null);
    // fileControl?.setValue(file);
    const formData = new FormData();

    formData.append('file', this.formArray.at(index).get('transcriptfile')?.value);
    formData.append('Description', 'academic_transcript');
    formData.append('filename', this.formArray.at(index).get('transcriptfile')?.value?.name);
    formData.append('ResponseValue', index.toString());

    const responseData = await this.newRepApplicationService
      .uploadDocuments(formData)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.formArray.at(index).patchValue({
        transcriptfileName: responseData.data.responsedata
      });
      this.viewDocumants();
      this.snackBarService.openSuccessSnackBar("document uploaded successfully!");
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
    }
  }

  async oncertifivateFileChange(event: any, index: number): Promise<void> {
    const file = event.target.files?.[0];
    const fileControl = this.formArray.at(index).get('certificatefileName');
    if (file) {
      this.formArray.at(index).patchValue({
        certificatefile: file,
        certificatefileName: file.name
      });
    }
    fileControl?.markAsTouched();
    fileControl?.updateValueAndValidity();
    if (!file) {
      fileControl?.setValue(null);
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // ❌ Invalid type
    if (!allowedTypes.includes(file.type)) {
      fileControl?.setErrors({ invalidType: true });
      return;
    }

    // ❌ Invalid size
    if (file.size > maxSize) {
      fileControl?.setErrors({ maxSize: true });
      return;
    }

    // ✅ Valid file
    fileControl?.setErrors(null);
    // fileControl?.setValue(file);
    const formData = new FormData();

    formData.append('file', this.formArray.at(index).get('certificatefile')?.value);
    formData.append('Description', 'Academic');
    formData.append('filename', this.formArray.at(index).get('certificatefile')?.value?.name);
    formData.append('ResponseValue', index.toString());

    const responseData = await this.newRepApplicationService
      .uploadDocuments(formData)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.formArray.at(index).patchValue({
        certificatefileName: responseData.data.responsedata
      });
      this.viewDocumants();
      this.snackBarService.openSuccessSnackBar("document uploaded successfully!");
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
    }
  }


  async viewDocumants() {
    let id = Number(this.sessionStorageService.getCurrentUserId());
    const responseData = await this.newRepApplicationService
      .viewApplicationDetails(id)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.viewattachments = responseData.data.responsedata.attachments;
  }


  setstartDateMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>, index: number) {
    const control = this.formArray.at(index).get('startDate');
    const ctrlValue = moment(control?.value || new Date());
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    control?.setValue(ctrlValue);
    datepicker.close();
  }

  setendDateMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>, index: number) {
    const control = this.formArray.at(index).get('endDate');
    const ctrlValue = moment(control?.value || new Date());
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    control?.setValue(ctrlValue);
    datepicker.close();
  }

  removeFile(controlName: string, fileVar: string, categoryname: string, index: number) {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });

    this.viewDocumants();
    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.viewattachments) {
          let id;
          let docTypeId;
          let onboardingId;
          this.viewattachments.forEach(item => {
            if (item?.onboardingDocumentType === categoryname && index === Number(item?.responseValue)) {
              id = item.id,
                docTypeId = item.docTypeId,
                onboardingId = item.onboardingId
            }
          });

          const data = {
            onboardingId: onboardingId,
            docTypeId: docTypeId,
            id: id
          }
          const responseData = await this.newRepApplicationService
            .deletedocument(data)
            .then((response) => {
              return JSON.parse(response);
            })
            .catch((error) => {
              // console.error(error);
              return error;
            });
          this.formArray.at(index).get(controlName)?.reset();
          const fileInput = document.getElementById(controlName) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
          this.formArray.at(index).get(fileVar)?.reset();
          this.formArray.at(index).get(controlName)?.markAsTouched();
          this.formArray.at(index).get(controlName)?.updateValueAndValidity();
        }
      } else {
        // console.log('User cancelled');
      }
    });
  }

  handleFormStatus() {
    if (this.readonly()) return;

    const initialValue = this.formGroup.getRawValue();

    this.formGroup.valueChanges.subscribe(currentValue => {
      const hasChanged =
        JSON.stringify(initialValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'change']);
      }
    });
  }

  //  Submit
  saveAndContinue(): void {

    // Save API call or form validation here
    if (this.formGroup.valid) {
      this.status = 'Completed';

    }
    this.formGroup.markAllAsTouched();
    this.next.emit([this.status, 'submit']);
  }

  previewAttachment(controlName: string, index: number) {
    if (this.viewattachments) {
      let id;
      this.viewattachments.forEach(item => {
        if (item?.onboardingDocumentType === 'Academic' && index === Number(item?.responseValue)) {
          id = item.id
        }
      });
      let filename = this.formArray.at(index).get(controlName)?.value;
      this.preview.emit([id, filename]);
    }
  }

  previewtranscriptAttachment(controlName: string, index: number) {
    if (this.viewattachments) {
      let id;
      this.viewattachments.forEach(item => {
        if (item?.onboardingDocumentType === 'academic_transcript' && index === Number(item?.responseValue)) {
          id = item.id
        }
      });
      let filename = this.formArray.at(index).get(controlName)?.value;
      this.preview.emit([id, filename]);
    }
  }
}
